import { KYC_STATUS_LOCAL, PROVIDER_STATUS } from "constants/status";
import moment from "moment";
import { createSelector } from "reselect";

const getKYC = (state) => state.kyc.kycData;
const getKYCLoading = (state) => state.kyc.isSubmittingKYC;
const getKycSubmitSuccess = (state) => state.kyc.isSubmittingKycSuccess;
const getReKycLoading = (state) => state.kyc.isSubmittingReKyc;
const getReKycSubmitSuccess = (state) => state.kyc.isSubmittingReKycSuccess;
const getKYCError = (state) => state.kyc.error;
export const getUserInitiated = (state) => state.kyc.hasUserInitiated;
const getUserKYCStatus = (state) => state.kyc.statusKYC;
const getReKycStatus = (state) => state.kyc.reKYC;
const getUserKYCed = (state) => state.kyc.isUserKYCed;
const getKycFormData = (state) => state.kyc.kycFormData;
const getKycProvider = (state) => state.kyc?.providerData;

const getUserKYCServerError = (state) => state.kyc.isKYCServerError;

//ADMIN
const getAdminKYC = (state) => state.kyc.adminSelectedUser.kycData;
const getAdminUserInitiated = (state) => state.kyc?.adminSelectedUser?.hasUserInitiated;
const getAdminReKycStatus = (state) => state.kyc?.adminSelectedUser?.reKYC;
const getAdminUserKYCed = (state) => state.kyc?.adminSelectedUser?.isUserKYCed;
const getAdminKycProvider = (state) => state.kyc.adminSelectedUser?.providerData;
const getKycFormDataAdmin = (state) => state.kyc.adminSelectedUser?.kycFormData;
const getKycAdminLoading = (state) => state.kyc.adminSelectedUser?.isLoading;

const finalizeKYCStatus = (hasUserInitiated, isUserKYCed, reKYC, statusKYC) => {
    if (isUserKYCed && !reKYC) return KYC_STATUS_LOCAL.COMPLETED;
    if (statusKYC === PROVIDER_STATUS.FAILURE) return KYC_STATUS_LOCAL.ERROR;

    if (reKYC) {
        if (statusKYC === PROVIDER_STATUS.CREATED) return KYC_STATUS_LOCAL.INITIATED;
        return KYC_STATUS_LOCAL.REKYC;
    }

    if (hasUserInitiated) return KYC_STATUS_LOCAL.INITIATED;

    return KYC_STATUS_LOCAL.INCOMPLETE;
};

const getKYCExpired = (completedAt, idDocumentExpiryDate) => {
    const now = moment();

    const completed = moment(completedAt);
    const idExpiry = moment(idDocumentExpiryDate);

    // Condition 1: ID document expired
    const isIDExpired = idExpiry.isBefore(now, "day");

    // Condition 2: KYC completed more than 2 years ago
    const isKYCTooOld = now.diff(completed, "years", true) >= 2;

    return isIDExpired || isKYCTooOld;
};

export const selectKycExpired = createSelector(getKycProvider, (providerData) => {
    return providerData ? getKYCExpired(providerData.CompletedAt, providerData.IDDocumentExpiryDate) : false;
});

export const selectKYC = createSelector(getKYC, (kycData) => kycData);

export const selectKycFormData = createSelector(getKycFormData, (kycData) => kycData);

export const selectKycProviderData = createSelector(getKycProvider, (providerData) => providerData);

export const selectKYCSubmitting = createSelector(getKYCLoading, (isSubmittingKYC) => isSubmittingKYC);
export const selectKycSubmittingSuccess = createSelector(getKycSubmitSuccess, (isSubmitKycSuccess) => isSubmitKycSuccess);
export const selectReKycSubmitting = createSelector(getReKycLoading, (isSubmittingReKyc) => isSubmittingReKyc);
export const selectReKycSubmittingSuccess = createSelector(getReKycSubmitSuccess, (isSubmitReKycSuccess) => isSubmitReKycSuccess);
export const selectKYCError = createSelector(getKYCError, (error) => error);
export const selectKYCIsAccepted = createSelector(getKYC, (kyc) => kyc?.isAccepted);
export const selectHasUserInitiated = createSelector(getUserInitiated, (hasInitiated) => hasInitiated);
export const selectHasUserInitiatedAdmin = createSelector(getAdminUserInitiated, (hasInitiated) => hasInitiated);

export const selectReKycStatus = createSelector(getReKycStatus, (reKycStatus) => reKycStatus);

export const selectCurrenKYCStatus = createSelector(
    [getUserInitiated, getUserKYCed, getReKycStatus, getUserKYCStatus],
    finalizeKYCStatus,
);

export const selectKYCStatusShow = createSelector(selectCurrenKYCStatus, (currentKYCStatus) => {
    return currentKYCStatus !== KYC_STATUS_LOCAL.INCOMPLETE;
});

export const selectKYCCompleted = createSelector(selectCurrenKYCStatus, (currentKYCStatus) => {
    return currentKYCStatus === KYC_STATUS_LOCAL.COMPLETED;
});

//ADMIN
export const selectKycFormDataAdmin = createSelector(getKycFormDataAdmin, (kycFormData) => kycFormData);
export const selectAdminKYC = createSelector(getAdminKYC, (kycData) => kycData);
export const selectAdminReKYCStatus = createSelector(getAdminReKycStatus, (reKYC) => reKYC);
export const selectAdminKYCProvider = createSelector(getAdminKycProvider, (providerData) => providerData?.Provider);
export const selectAdminKYCProviderData = createSelector(getAdminKycProvider, (providerData) => providerData);
export const selectAdminKYCLoading = createSelector(getKycAdminLoading, (isLoading) => isLoading);

export const selectKYCServerError = createSelector(getUserKYCServerError, (isKYCServerError) => isKYCServerError);

export const selectCurrenKYCStatusAdmin = createSelector(
    [getAdminUserInitiated, getAdminUserKYCed, getAdminReKycStatus],
    finalizeKYCStatus,
);

export const selectKYCStatusShowAdmin = createSelector(selectCurrenKYCStatusAdmin, (currentKYCStatus) => {
    return (
        currentKYCStatus !== KYC_STATUS_LOCAL.INCOMPLETE ||
        currentKYCStatus !== KYC_STATUS_LOCAL.INITIATED ||
        currentKYCStatus !== KYC_STATUS_LOCAL.REKYC
    );
});
